import { authService } from '@utils/auth/authService'
import { HTTP_STATUS } from '@utils/constants/http'
import axios from 'axios'

/**
 * Builds the Authorization header from the current auth token.
 * Mirrors the request interceptor so it can be reused for SSE / fetch.
 *
 * @returns {Object} `{ Authorization: "Bearer <token>" }` if a token is set, otherwise `{}`.
 */
export const getAuthHeaders = () => {
  const token = authService.getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Handles 401 / 403 responses outside of axios (e.g. for SSE / fetch).
 * Mirrors the response interceptor.
 *
 * @param {number} status - HTTP status code from the response.
 * @returns {void}
 */
export const handleAuthError = status => {
  if (status === HTTP_STATUS.UNAUTHORIZED) {
    authService.clearToken()
    window.dispatchEvent(new CustomEvent('auth:unauthorized'))
  }

  if (status === HTTP_STATUS.FORBIDDEN) {
    window.dispatchEvent(new CustomEvent('auth:forbidden'))
  }
}

/**
 * Creates an axios instance configured with the base URL, default headers,
 * and request/response interceptors for auth header injection and 401/403 handling.
 *
 * @param {string} [baseURL=window.BACKEND_URL] - Base URL for the axios instance.
 * @returns {Object} Configured axios instance.
 */
const createApiClient = (baseURL = window.BACKEND_URL) => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8'
    },
    paramsSerializer: {
      indexes: null
    }
  })

  client.interceptors.request.use(
    config => {
      Object.assign(config.headers, getAuthHeaders())
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  client.interceptors.response.use(
    response => response,
    error => {
      handleAuthError(error.response?.status)
      return Promise.reject(error)
    }
  )

  return client
}

/**
 * Axios instance configured for the main backend API.
 */
export const apiClient = createApiClient(window.BACKEND_URL)

/**
 * Axios instance configured for the deploy service.
 */
export const httpDeployClient = createApiClient(window.HTTP_DEPLOY_URL)

/**
 * Axios instance configured for HawkBit (OTA).
 */
export const hawkbitClient = createApiClient(window.HAWKBIT_URL)

export default apiClient
